import {
  AppointmentResponse,
  AppointmentStatus,
  PreAuthStatus,
} from '@dataTypes/appointment.type'
import { NoteContextType } from '@dataTypes/note.type'
import moment from 'moment'

/** Assigned room labels aligned with Patient Profile appointments table (Figma). */
const MOCK_APPOINTMENT_ROOM_NAMES: Record<string, string> = {
  'mock-appt-1-1': 'Room 102',
  'mock-appt-1-2': 'Room 150',
  'mock-appt-1-3': 'Room 421',
  'mock-appt-1-4': 'Room 193',
  'mock-appt-1-5': 'Room 205',
  'mock-appt-1-6': 'Room 310',
  'mock-appt-1-7': 'Room 102',
  'mock-appt-1-8': 'Room 218',
  'mock-appt-2-1': 'Room 421',
  'mock-appt-2-2': 'Room 102',
  'mock-appt-2-3': 'Room 150',
  'mock-appt-2-4': 'Room 193',
  'mock-appt-2-5': 'Room 205',
  'mock-appt-2-6': 'Room 310',
  'mock-appt-2-7': 'Room 421',
}

/** Pre-auth status labels aligned with Patient Profile appointments table (Figma). */
const MOCK_APPOINTMENT_PRE_AUTH_STATUSES: Record<string, PreAuthStatus> = {
  'mock-appt-1-1': 'approved',
  'mock-appt-1-2': 'na',
  'mock-appt-1-3': 'pending',
  'mock-appt-1-4': 'pending',
  'mock-appt-1-5': 'pending',
  'mock-appt-1-6': 'na',
  'mock-appt-1-7': 'verified',
  'mock-appt-1-8': 'na',
  'mock-appt-2-1': 'approved',
  'mock-appt-2-2': 'na',
  'mock-appt-2-3': 'pending',
  'mock-appt-2-4': 'denied',
  'mock-appt-2-5': 'approved',
  'mock-appt-2-6': 'verified',
  'mock-appt-2-7': 'pending',
}

function enrichMockAppointment(
  appointment: AppointmentResponse,
): AppointmentResponse {
  const roomName = MOCK_APPOINTMENT_ROOM_NAMES[appointment.appointment_id]
  const preAuthStatus =
    MOCK_APPOINTMENT_PRE_AUTH_STATUSES[appointment.appointment_id]

  if (!roomName && !preAuthStatus) return appointment

  return {
    ...appointment,
    ...(roomName
      ? {
          operatory: {
            operatory_id: appointment.operatory_id,
            name: roomName,
          },
        }
      : {}),
    ...(preAuthStatus ? { pre_auth_status: preAuthStatus } : {}),
  }
}

const rawMockAppointments: Record<string, AppointmentResponse[]> = {
  'mock-patient-1': [
    {
      appointment_id: 'mock-appt-1-1',
      patient_id: 'mock-patient-1',
      clinic_id: 'clinic-1',
      operatory_id: 'op-1',
      insurance_id: 'ins-1',
      provider_id: 'provider-1',
      provider_hygienist: 'hygienist-1',
      appointment_type_id: 'type-1',
      appointment_type_subcategory_id: 'subcat-1',
      appt_status: AppointmentStatus.Completed,
      appt_date: moment().subtract(2, 'months').format('YYYY-MM-DD'),
      appt_start: '09:00:00',
      appt_end: '10:00:00',
      duration: 60,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['phone', 'email'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-1',
      updated_by: 'user-1',
      is_hidden: false,
      date_time_created_at: moment().subtract(3, 'months').toISOString(),
      date_time_updated_at: moment().subtract(2, 'months').toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-1',
        first_name: 'John',
        last_name: 'Doe',
        cellphone: '555-0101',
      },
      provider: {
        provider_id: 'provider-1',
        first_name: 'Sarah',
        last_name: 'Johnson',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-1',
        name: 'Cleaning',
      },
      procedure_logs: [
        {
          proc_code: 'D1110',
          description: 'Adult prophylaxis',
        },
      ],
      lab_orders: [],
      notes: [
        {
          note_id: '1',
          patient_id: 'mock-patient-1',
          clinic_id: 'clinic-1',
          title: 'Appointment note',
          body: 'Regular cleaning completed. Patient has good oral hygiene.',
          context_type: NoteContextType.APPOINTMENT,
          context_id: 'mock-appt-1-1',
          created_by: 'user-1',
          updated_by: 'user-1',
          date_time_created_at: moment().subtract(2, 'months').toISOString(),
          date_time_updated_at: moment().subtract(2, 'months').toISOString(),
        },
      ],
      Id: 1,
      appointment_start_date: moment()
        .subtract(2, 'months')
        .format('YYYY-MM-DD'),
      appointment_end_date: moment().subtract(2, 'months').format('YYYY-MM-DD'),
    },
    {
      appointment_id: 'mock-appt-1-2',
      patient_id: 'mock-patient-1',
      clinic_id: 'clinic-1',
      operatory_id: 'op-2',
      insurance_id: 'ins-1',
      provider_id: 'provider-1',
      provider_hygienist: null,
      appointment_type_id: 'type-2',
      appointment_type_subcategory_id: 'subcat-2',
      appt_status: AppointmentStatus.Completed,
      appt_date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
      appt_start: '14:00:00',
      appt_end: '15:30:00',
      duration: 90,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['phone', 'sms'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-1',
      updated_by: 'user-1',
      is_hidden: false,
      date_time_created_at: moment().subtract(2, 'months').toISOString(),
      date_time_updated_at: moment().subtract(1, 'month').toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-1',
        first_name: 'John',
        last_name: 'Doe',
        cellphone: '555-0101',
      },
      provider: {
        provider_id: 'provider-1',
        first_name: 'Sarah',
        last_name: 'Johnson',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-2',
        name: 'Examination',
      },
      procedure_logs: [
        {
          proc_code: 'D0150',
          description: 'Comprehensive oral evaluation',
        },
      ],
      lab_orders: [],
      notes: [
        {
          note_id: '2',
          patient_id: 'mock-patient-1',
          clinic_id: 'clinic-1',
          title: 'Appointment note',
          body: 'Routine checkup. No issues found.',
          context_type: NoteContextType.APPOINTMENT,
          context_id: 'mock-appt-1-2',
          created_by: 'user-1',
          updated_by: 'user-1',
          date_time_created_at: moment().subtract(1, 'month').toISOString(),
          date_time_updated_at: moment().subtract(1, 'month').toISOString(),
        },
      ],
      Id: 2,
      appointment_start_date: moment()
        .subtract(1, 'month')
        .format('YYYY-MM-DD'),
      appointment_end_date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
    },
    {
      appointment_id: 'mock-appt-1-3',
      patient_id: 'mock-patient-1',
      clinic_id: 'clinic-1',
      operatory_id: 'op-1',
      insurance_id: 'ins-1',
      provider_id: 'provider-1',
      provider_hygienist: 'hygienist-1',
      appointment_type_id: 'type-1',
      appointment_type_subcategory_id: 'subcat-1',
      appt_status: AppointmentStatus.Confirmed,
      appt_date: moment().add(2, 'weeks').format('YYYY-MM-DD'),
      appt_start: '10:00:00',
      appt_end: '11:00:00',
      duration: 60,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['phone', 'email'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-1',
      updated_by: 'user-1',
      is_hidden: false,
      date_time_created_at: moment().subtract(1, 'week').toISOString(),
      date_time_updated_at: moment().subtract(1, 'week').toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-1',
        first_name: 'John',
        last_name: 'Doe',
        cellphone: '555-0101',
      },
      provider: {
        provider_id: 'provider-1',
        first_name: 'Sarah',
        last_name: 'Johnson',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-1',
        name: 'Cleaning',
      },
      procedure_logs: [],
      lab_orders: [],
      notes: [],
      Id: 3,
      appointment_start_date: moment().add(2, 'weeks').format('YYYY-MM-DD'),
      appointment_end_date: moment().add(2, 'weeks').format('YYYY-MM-DD'),
    },
    {
      appointment_id: 'mock-appt-1-4',
      patient_id: 'mock-patient-1',
      clinic_id: 'clinic-1',
      operatory_id: 'op-4',
      insurance_id: 'ins-1',
      provider_id: 'provider-1',
      provider_hygienist: 'hygienist-1',
      appointment_type_id: 'type-2',
      appointment_type_subcategory_id: 'subcat-2',
      appt_status: AppointmentStatus.NoShow,
      appt_date: moment().subtract(4, 'months').format('YYYY-MM-DD'),
      appt_start: '11:00:00',
      appt_end: '11:30:00',
      duration: 30,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['sms', 'email'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-1',
      updated_by: 'user-1',
      is_hidden: false,
      date_time_created_at: moment().subtract(5, 'months').toISOString(),
      date_time_updated_at: moment().subtract(4, 'months').toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-1',
        first_name: 'John',
        last_name: 'Doe',
        cellphone: '555-0101',
      },
      provider: {
        provider_id: 'provider-1',
        first_name: 'Sarah',
        last_name: 'Johnson',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-2',
        name: 'Examination',
      },
      procedure_logs: [],
      lab_orders: [],
      notes: [
        {
          note_id: '8',
          patient_id: 'mock-patient-1',
          clinic_id: 'clinic-1',
          title: 'Appointment note',
          body: 'Patient did not arrive; marked as no-show.',
          context_type: NoteContextType.APPOINTMENT,
          context_id: 'mock-appt-1-4',
          created_by: 'user-1',
          updated_by: 'user-1',
          date_time_created_at: moment().subtract(4, 'months').toISOString(),
          date_time_updated_at: moment().subtract(4, 'months').toISOString(),
        },
      ],
      Id: 14,
      appointment_start_date: moment()
        .subtract(4, 'months')
        .format('YYYY-MM-DD'),
      appointment_end_date: moment().subtract(4, 'months').format('YYYY-MM-DD'),
    },
    {
      appointment_id: 'mock-appt-1-5',
      patient_id: 'mock-patient-1',
      clinic_id: 'clinic-1',
      operatory_id: 'op-5',
      insurance_id: 'ins-1',
      provider_id: 'provider-1',
      provider_hygienist: null,
      appointment_type_id: 'type-3',
      appointment_type_subcategory_id: 'subcat-3',
      appt_status: AppointmentStatus.Pending,
      appt_date: moment().add(6, 'weeks').format('YYYY-MM-DD'),
      appt_start: '15:15:00',
      appt_end: '16:00:00',
      duration: 45,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['phone'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-1',
      updated_by: 'user-1',
      is_hidden: false,
      date_time_created_at: moment().toISOString(),
      date_time_updated_at: moment().toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-1',
        first_name: 'John',
        last_name: 'Doe',
        cellphone: '555-0101',
      },
      provider: {
        provider_id: 'provider-1',
        first_name: 'Sarah',
        last_name: 'Johnson',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-3',
        name: 'Root Canal',
      },
      procedure_logs: [],
      lab_orders: [],
      notes: [],
      Id: 15,
      appointment_start_date: moment().add(6, 'weeks').format('YYYY-MM-DD'),
      appointment_end_date: moment().add(6, 'weeks').format('YYYY-MM-DD'),
    },
    {
      appointment_id: 'mock-appt-1-6',
      patient_id: 'mock-patient-1',
      clinic_id: 'clinic-1',
      operatory_id: 'op-2',
      insurance_id: 'ins-1',
      provider_id: 'provider-1',
      provider_hygienist: null,
      appointment_type_id: 'type-2',
      appointment_type_subcategory_id: 'subcat-2',
      appt_status: AppointmentStatus.Unconfirmed,
      appt_date: moment().add(10, 'days').format('YYYY-MM-DD'),
      appt_start: '13:00:00',
      appt_end: '13:45:00',
      duration: 45,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['phone', 'sms'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-1',
      updated_by: 'user-1',
      is_hidden: false,
      date_time_created_at: moment().toISOString(),
      date_time_updated_at: moment().toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-1',
        first_name: 'John',
        last_name: 'Doe',
        cellphone: '555-0101',
      },
      provider: {
        provider_id: 'provider-1',
        first_name: 'Sarah',
        last_name: 'Johnson',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-2',
        name: 'Examination',
      },
      procedure_logs: [],
      lab_orders: [],
      notes: [],
      Id: 18,
      appointment_start_date: moment().add(10, 'days').format('YYYY-MM-DD'),
      appointment_end_date: moment().add(10, 'days').format('YYYY-MM-DD'),
    },
    {
      appointment_id: 'mock-appt-1-7',
      patient_id: 'mock-patient-1',
      clinic_id: 'clinic-1',
      operatory_id: 'op-3',
      insurance_id: 'ins-1',
      provider_id: 'provider-1',
      provider_hygienist: 'hygienist-1',
      appointment_type_id: 'type-1',
      appointment_type_subcategory_id: 'subcat-1',
      appt_status: AppointmentStatus.CheckIn,
      appt_date: moment().format('YYYY-MM-DD'),
      appt_start: '09:15:00',
      appt_end: '10:00:00',
      duration: 45,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['phone', 'email'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-1',
      updated_by: 'user-1',
      is_hidden: false,
      date_time_created_at: moment().toISOString(),
      date_time_updated_at: moment().toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-1',
        first_name: 'John',
        last_name: 'Doe',
        cellphone: '555-0101',
      },
      provider: {
        provider_id: 'provider-1',
        first_name: 'Sarah',
        last_name: 'Johnson',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-1',
        name: 'Cleaning',
      },
      procedure_logs: [],
      lab_orders: [],
      notes: [
        {
          note_id: '9',
          patient_id: 'mock-patient-1',
          clinic_id: 'clinic-1',
          title: 'Appointment note',
          body: 'Patient checked in and waiting in lobby.',
          context_type: NoteContextType.APPOINTMENT,
          context_id: 'mock-appt-1-7',
          created_by: 'user-1',
          updated_by: 'user-1',
          date_time_created_at: moment().toISOString(),
          date_time_updated_at: moment().toISOString(),
        },
      ],
      Id: 19,
      appointment_start_date: moment().format('YYYY-MM-DD'),
      appointment_end_date: moment().format('YYYY-MM-DD'),
    },
    {
      appointment_id: 'mock-appt-1-8',
      patient_id: 'mock-patient-1',
      clinic_id: 'clinic-1',
      operatory_id: 'op-3',
      insurance_id: 'ins-1',
      provider_id: 'provider-1',
      provider_hygienist: null,
      appointment_type_id: 'type-3',
      appointment_type_subcategory_id: 'subcat-3',
      appt_status: AppointmentStatus.Cancelled,
      appt_date: moment().subtract(3, 'weeks').format('YYYY-MM-DD'),
      appt_start: '11:30:00',
      appt_end: '12:30:00',
      duration: 60,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['phone', 'sms'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-1',
      updated_by: 'user-1',
      is_hidden: false,
      date_time_created_at: moment().subtract(1, 'month').toISOString(),
      date_time_updated_at: moment().subtract(3, 'weeks').toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-1',
        first_name: 'John Mark',
        last_name: 'Doe',
        cellphone: '555-0101',
      },
      provider: {
        provider_id: 'provider-1',
        first_name: 'Sarah',
        last_name: 'Johnson',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-3',
        name: 'Root Canal',
      },
      procedure_logs: [],
      lab_orders: [],
      notes: [
        {
          note_id: '12',
          patient_id: 'mock-patient-1',
          clinic_id: 'clinic-1',
          title: 'Appointment note',
          body: 'Patient cancelled; reschedule when insurance pre-auth is ready.',
          context_type: NoteContextType.APPOINTMENT,
          context_id: 'mock-appt-1-8',
          created_by: 'user-1',
          updated_by: 'user-1',
          date_time_created_at: moment().subtract(3, 'weeks').toISOString(),
          date_time_updated_at: moment().subtract(3, 'weeks').toISOString(),
        },
      ],
      Id: 22,
      appointment_start_date: moment()
        .subtract(3, 'weeks')
        .format('YYYY-MM-DD'),
      appointment_end_date: moment().subtract(3, 'weeks').format('YYYY-MM-DD'),
    },
  ],
  'mock-patient-2': [
    {
      appointment_id: 'mock-appt-2-1',
      patient_id: 'mock-patient-2',
      clinic_id: 'clinic-1',
      operatory_id: 'op-3',
      insurance_id: 'ins-2',
      provider_id: 'provider-2',
      provider_hygienist: null,
      appointment_type_id: 'type-3',
      appointment_type_subcategory_id: 'subcat-3',
      appt_status: AppointmentStatus.Completed,
      appt_date: moment().subtract(3, 'months').format('YYYY-MM-DD'),
      appt_start: '11:00:00',
      appt_end: '12:00:00',
      duration: 60,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['phone', 'email'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-2',
      updated_by: 'user-2',
      is_hidden: false,
      date_time_created_at: moment().subtract(4, 'months').toISOString(),
      date_time_updated_at: moment().subtract(3, 'months').toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-2',
        first_name: 'Jane',
        last_name: 'Smith',
        cellphone: '555-0202',
      },
      provider: {
        provider_id: 'provider-2',
        first_name: 'Michael',
        last_name: 'Rodriguez',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-3',
        name: 'Root Canal',
      },
      procedure_logs: [
        {
          proc_code: 'D3310',
          description: 'Root canal therapy',
        },
      ],
      lab_orders: [],
      notes: [
        {
          note_id: '3',
          patient_id: 'mock-patient-2',
          clinic_id: 'clinic-1',
          title: 'Appointment note',
          body: 'Root canal procedure completed successfully. Patient tolerated well.',
          context_type: NoteContextType.APPOINTMENT,
          context_id: 'mock-appt-2-1',
          created_by: 'user-2',
          updated_by: 'user-2',
          date_time_created_at: moment().subtract(3, 'months').toISOString(),
          date_time_updated_at: moment().subtract(3, 'months').toISOString(),
        },
      ],
      Id: 4,
      appointment_start_date: moment()
        .subtract(3, 'months')
        .format('YYYY-MM-DD'),
      appointment_end_date: moment().subtract(3, 'months').format('YYYY-MM-DD'),
    },
    {
      appointment_id: 'mock-appt-2-2',
      patient_id: 'mock-patient-2',
      clinic_id: 'clinic-1',
      operatory_id: 'op-2',
      insurance_id: 'ins-2',
      provider_id: 'provider-2',
      provider_hygienist: 'hygienist-2',
      appointment_type_id: 'type-1',
      appointment_type_subcategory_id: 'subcat-1',
      appt_status: AppointmentStatus.Completed,
      appt_date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
      appt_start: '09:30:00',
      appt_end: '10:30:00',
      duration: 60,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['phone', 'sms', 'email'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-2',
      updated_by: 'user-2',
      is_hidden: false,
      date_time_created_at: moment().subtract(2, 'months').toISOString(),
      date_time_updated_at: moment().subtract(1, 'month').toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-2',
        first_name: 'Jane',
        last_name: 'Smith',
        cellphone: '555-0202',
      },
      provider: {
        provider_id: 'provider-2',
        first_name: 'Michael',
        last_name: 'Rodriguez',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-1',
        name: 'Cleaning',
      },
      procedure_logs: [
        {
          proc_code: 'D1110',
          description: 'Adult prophylaxis',
        },
      ],
      lab_orders: [],
      notes: [
        {
          note_id: '4',
          patient_id: 'mock-patient-2',
          clinic_id: 'clinic-1',
          title: 'Appointment note',
          body: 'Follow-up cleaning after root canal. All good.',
          context_type: NoteContextType.APPOINTMENT,
          context_id: 'mock-appt-2-2',
          created_by: 'user-2',
          updated_by: 'user-2',
          date_time_created_at: moment().subtract(1, 'month').toISOString(),
          date_time_updated_at: moment().subtract(1, 'month').toISOString(),
        },
      ],
      Id: 5,
      appointment_start_date: moment()
        .subtract(1, 'month')
        .format('YYYY-MM-DD'),
      appointment_end_date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
    },
    {
      appointment_id: 'mock-appt-2-3',
      patient_id: 'mock-patient-2',
      clinic_id: 'clinic-1',
      operatory_id: 'op-1',
      insurance_id: 'ins-2',
      provider_id: 'provider-2',
      provider_hygienist: null,
      appointment_type_id: 'type-2',
      appointment_type_subcategory_id: 'subcat-2',
      appt_status: AppointmentStatus.Confirmed,
      appt_date: moment().add(1, 'month').format('YYYY-MM-DD'),
      appt_start: '15:00:00',
      appt_end: '16:00:00',
      duration: 60,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['phone', 'email'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-2',
      updated_by: 'user-2',
      is_hidden: false,
      date_time_created_at: moment().subtract(2, 'weeks').toISOString(),
      date_time_updated_at: moment().subtract(2, 'weeks').toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-2',
        first_name: 'Jane',
        last_name: 'Smith',
        cellphone: '555-0202',
      },
      provider: {
        provider_id: 'provider-2',
        first_name: 'Michael',
        last_name: 'Rodriguez',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-2',
        name: 'Examination',
      },
      procedure_logs: [],
      lab_orders: [],
      notes: [],
      Id: 6,
      appointment_start_date: moment().add(1, 'month').format('YYYY-MM-DD'),
      appointment_end_date: moment().add(1, 'month').format('YYYY-MM-DD'),
    },
    {
      appointment_id: 'mock-appt-2-4',
      patient_id: 'mock-patient-2',
      clinic_id: 'clinic-1',
      operatory_id: 'op-1',
      insurance_id: 'ins-2',
      provider_id: 'provider-2',
      provider_hygienist: null,
      appointment_type_id: 'type-4',
      appointment_type_subcategory_id: 'subcat-4',
      appt_status: AppointmentStatus.Completed,
      appt_date: moment().subtract(5, 'months').format('YYYY-MM-DD'),
      appt_start: '10:45:00',
      appt_end: '11:30:00',
      duration: 45,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['email'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-2',
      updated_by: 'user-2',
      is_hidden: false,
      date_time_created_at: moment().subtract(6, 'months').toISOString(),
      date_time_updated_at: moment().subtract(5, 'months').toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-2',
        first_name: 'Jane',
        last_name: 'Smith',
        cellphone: '555-0202',
      },
      provider: {
        provider_id: 'provider-2',
        first_name: 'Michael',
        last_name: 'Rodriguez',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-4',
        name: 'Filling',
      },
      procedure_logs: [
        {
          proc_code: 'D2392',
          description: 'Resin-based composite',
        },
      ],
      lab_orders: [],
      notes: [],
      Id: 16,
      appointment_start_date: moment()
        .subtract(5, 'months')
        .format('YYYY-MM-DD'),
      appointment_end_date: moment().subtract(5, 'months').format('YYYY-MM-DD'),
    },
    {
      appointment_id: 'mock-appt-2-5',
      patient_id: 'mock-patient-2',
      clinic_id: 'clinic-1',
      operatory_id: 'op-5',
      insurance_id: 'ins-2',
      provider_id: 'provider-2',
      provider_hygienist: 'hygienist-2',
      appointment_type_id: 'type-6',
      appointment_type_subcategory_id: 'subcat-6',
      appt_status: AppointmentStatus.Late,
      appt_date: moment().add(2, 'months').format('YYYY-MM-DD'),
      appt_start: '08:15:00',
      appt_end: '09:00:00',
      duration: 45,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['sms', 'email'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-2',
      updated_by: 'user-2',
      is_hidden: false,
      date_time_created_at: moment().toISOString(),
      date_time_updated_at: moment().toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-2',
        first_name: 'Jane',
        last_name: 'Smith',
        cellphone: '555-0202',
      },
      provider: {
        provider_id: 'provider-2',
        first_name: 'Michael',
        last_name: 'Rodriguez',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-6',
        name: 'Emergency Exam',
      },
      procedure_logs: [],
      lab_orders: [],
      notes: [],
      Id: 17,
      appointment_start_date: moment().add(2, 'months').format('YYYY-MM-DD'),
      appointment_end_date: moment().add(2, 'months').format('YYYY-MM-DD'),
    },
    {
      appointment_id: 'mock-appt-2-6',
      patient_id: 'mock-patient-2',
      clinic_id: 'clinic-1',
      operatory_id: 'op-4',
      insurance_id: 'ins-2',
      provider_id: 'provider-2',
      provider_hygienist: null,
      appointment_type_id: 'type-2',
      appointment_type_subcategory_id: 'subcat-2',
      appt_status: AppointmentStatus.InTreatment,
      appt_date: moment().subtract(1, 'day').format('YYYY-MM-DD'),
      appt_start: '14:30:00',
      appt_end: '15:30:00',
      duration: 60,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['phone'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-2',
      updated_by: 'user-2',
      is_hidden: false,
      date_time_created_at: moment().subtract(2, 'days').toISOString(),
      date_time_updated_at: moment().subtract(1, 'day').toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-2',
        first_name: 'Jane',
        last_name: 'Smith',
        cellphone: '555-0202',
      },
      provider: {
        provider_id: 'provider-2',
        first_name: 'Michael',
        last_name: 'Rodriguez',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-2',
        name: 'Examination',
      },
      procedure_logs: [],
      lab_orders: [],
      notes: [
        {
          note_id: '10',
          patient_id: 'mock-patient-2',
          clinic_id: 'clinic-1',
          title: 'Appointment note',
          body: 'Patient in treatment; waiting on final bitewing check.',
          context_type: NoteContextType.APPOINTMENT,
          context_id: 'mock-appt-2-6',
          created_by: 'user-2',
          updated_by: 'user-2',
          date_time_created_at: moment().subtract(1, 'day').toISOString(),
          date_time_updated_at: moment().subtract(1, 'day').toISOString(),
        },
      ],
      Id: 20,
      appointment_start_date: moment().subtract(1, 'day').format('YYYY-MM-DD'),
      appointment_end_date: moment().subtract(1, 'day').format('YYYY-MM-DD'),
    },
    {
      appointment_id: 'mock-appt-2-7',
      patient_id: 'mock-patient-2',
      clinic_id: 'clinic-1',
      operatory_id: 'op-4',
      insurance_id: 'ins-2',
      provider_id: 'provider-2',
      provider_hygienist: null,
      appointment_type_id: 'type-1',
      appointment_type_subcategory_id: 'subcat-1',
      appt_status: AppointmentStatus.CheckOut,
      appt_date: moment().format('YYYY-MM-DD'),
      appt_start: '16:00:00',
      appt_end: '16:30:00',
      duration: 30,
      is_recurring: false,
      recurring_visit_id: null,
      recurring_every: null,
      recurring_for: null,
      recurring_days: null,
      recurring_start_date: null,
      recurring_end_date: null,
      is_insured: true,
      tp_id: null,
      files: [],
      template_id: null,
      contact_via: ['sms', 'email'],
      payment_status: 0,
      is_read_only: false,
      created_by: 'user-2',
      updated_by: 'user-2',
      is_hidden: false,
      date_time_created_at: moment().toISOString(),
      date_time_updated_at: moment().toISOString(),
      insurance_plan_id: null,
      procedure_id: null,
      patient: {
        patient_id: 'mock-patient-2',
        first_name: 'Jane',
        last_name: 'Smith',
        cellphone: '555-0202',
      },
      provider: {
        provider_id: 'provider-2',
        first_name: 'Michael',
        last_name: 'Rodriguez',
        provider_color: {
          header: '#185DD8',
          background: '#7d94fc',
          text_color: '#2dada9',
          label: '#086bd0',
        },
      },
      appointmenttype: {
        appointment_type_id: 'type-1',
        name: 'Cleaning',
      },
      procedure_logs: [],
      lab_orders: [],
      notes: [
        {
          note_id: '11',
          patient_id: 'mock-patient-2',
          clinic_id: 'clinic-1',
          title: 'Appointment note',
          body: 'Patient checked out; next hygiene visit scheduled.',
          context_type: NoteContextType.APPOINTMENT,
          context_id: 'mock-appt-2-7',
          created_by: 'user-2',
          updated_by: 'user-2',
          date_time_created_at: moment().toISOString(),
          date_time_updated_at: moment().toISOString(),
        },
      ],
      Id: 21,
      appointment_start_date: moment().format('YYYY-MM-DD'),
      appointment_end_date: moment().format('YYYY-MM-DD'),
    },
  ],
}

export const mockAppointments: Record<string, AppointmentResponse[]> =
  Object.fromEntries(
    Object.entries(rawMockAppointments).map(([patientId, appointments]) => [
      patientId,
      appointments.map(enrichMockAppointment),
    ]),
  )
